import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { 
	Box, 
	Typography, 
	Dialog, 
	DialogTitle, 
	DialogContent, 
	DialogActions, 
	Button, 
	Tabs, 
	Tab,
	TextField,
	Checkbox,
	IconButton,
	LinearProgress,
} from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { styled } from "@mui/material/styles";
// @ts-expect-error react-input-mask has no type definitions
import InputMask from "react-input-mask";
import SignatureCanvasComponent from "./SignatureCanvas";
import { deriveInitialsFromName } from "../../utils/initials";

dayjs.extend(customParseFormat);

const FormContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),
	padding: theme.spacing(0),
	backgroundColor: "white",
	borderRadius: theme.shape.borderRadius,
}));

const FieldGroupSection = styled(Box)(({ theme }) => ({
	marginBottom: theme.spacing(2.5),
	"&:last-of-type": {
		marginBottom: 0,
	},
}));

const FieldGroupTitle = styled(Typography)(() => ({
	fontSize: "14px",
	fontWeight: 600,
	color: "#374151",
	marginBottom: "12px",
	letterSpacing: "0.02em",
	textTransform: "uppercase",
}));

const ChoiceQuestionTitle = styled(Typography)(() => ({
	fontSize: "14px",
	fontWeight: 600,
	color: "#111827",
	marginBottom: "4px",
	lineHeight: 1.4,
}));

const FieldContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(0.5),
	marginBottom: theme.spacing(1.5),
}));

/** Compact input height and label hierarchy */
const compactInputSx = {
	"& .MuiInputBase-root": {
		height: 36,
		fontSize: "14px",
	},
	"& .MuiInputBase-input": {
		py: "8px",
		px: "12px",
	},
	"& .MuiOutlinedInput-notchedOutline": {
		top: 0,
	},
	"& .MuiInputLabel-outlined": {
		fontSize: "12px",
	},
};

const labelStyles = {
	fontSize: "12px",
	color: "#6b7280",
	fontWeight: 500,
	marginBottom: "4px",
	display: "block",
};


const RequiredIndicator = styled('span')(({ theme }) => ({
	color: theme.palette.error.main,
	marginLeft: theme.spacing(0.5),
}));

const SignatureField = styled(Box)(({ theme }) => ({
	border: `1px solid ${theme.palette.grey[300]}`,
	borderRadius: theme.shape.borderRadius,
	padding: theme.spacing(1),
	minHeight: "44px",
	display: "flex",
	alignItems: "center",
	cursor: "pointer",
	backgroundColor: theme.palette.grey[50],
	"&:hover": {
		backgroundColor: theme.palette.grey[100],
		borderColor: theme.palette.primary.main,
	},
}));

const SignaturePlaceholder = styled(Typography)(({ theme }) => ({
	color: theme.palette.text.secondary,
	fontStyle: "italic",
}));

const SignatureContent = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	width: "100%",
	height: "100%",
}));


const StyledDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		zIndex: 1300,
	},
	"& .MuiBackdrop-root": {
		zIndex: 1299,
	},
}));

interface ValueField {
	id: string;
	label: string;
	placeholder: string;
	value: string;
	fieldType: string;
	fieldRequired?: boolean;
	fieldGroupTitle?: string;
	choiceGroup?: string;
	autoGenerateInitials?: boolean;
}

type ChoiceFormRow =
	| { kind: "field"; field: ValueField }
	| { kind: "checkboxes"; question: string; fields: ValueField[] };

function clusterCheckboxQuestions(fields: ValueField[]): ChoiceFormRow[] {
	return fields.reduce<ChoiceFormRow[]>((rows, field) => {
		const question = (field.choiceGroup || "").trim();
		const last = rows[rows.length - 1];
		if (field.fieldType === "checkbox" && question && last && last.kind === "checkboxes" && last.question === question) {
			last.fields.push(field);
			return rows;
		}
		if (field.fieldType === "checkbox" && question) {
			rows.push({ kind: "checkboxes", question, fields: [field] });
			return rows;
		}
		rows.push({ kind: "field", field });
		return rows;
	}, []);
}

/** Normalize phone to XXX-XXX-XXXX (digits only, max 10). Accepts with or without dashes. */
function formatPhoneToXXX(value: string): string {
	const digits = (value || "").replace(/\D/g, "").slice(0, 10);
	if (digits.length <= 3) return digits;
	if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
	return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Detect if field is a phone number field (by type or label/description). */
function isPhoneField(field: ValueField): boolean {
	const lower = (field.label || "").toLowerCase();
	return (
		field.fieldType === "phone" ||
		lower.includes("phone") ||
		lower.includes("telephone") ||
		lower.includes("teléfono")
	);
}

interface DynamicFormProps {
	values: ValueField[];
	onChange: (fieldId: string, value: string, fieldType: string) => void;
	signatureImage: string | null;
	onSaveSignature?: (signatureData: string) => void;
	onFocus?: (fieldId: string) => void;
	onFieldInView?: (fieldId: string) => void;
	activeFieldId?: string | null;
	/** Profile / account name for initials suggestions (external app: `esignature` storage) */
	signerDisplayName?: string;
	readOnly?: boolean;
}

interface FieldRendererProps {
	field: ValueField;
	onChange: (fieldId: string, value: string, fieldType: string) => void;
	onSignatureClick: (fieldId: string) => void;
	onFocus?: (fieldId: string) => void;
	onFieldInView?: (fieldId: string) => void;
	compactInputSx: object;
	labelStyles: object;
	signerDisplayName?: string;
	readOnly?: boolean;
}

const formatReadOnlyValue = (fieldType: string, value: string) => {
	if (fieldType === "checkbox") return value === "true" ? "Yes" : "No";
	if (!value) return "—";
	return value;
};

const FieldRenderer: React.FC<FieldRendererProps> = ({ field, onChange, onSignatureClick, onFocus, onFieldInView, compactInputSx, labelStyles, signerDisplayName, readOnly }) => {
	const { id, fieldType, label, placeholder, value, fieldRequired } = field;
	const [localValue, setLocalValue] = useState(value);

	React.useEffect(() => {
		setLocalValue(value);
	}, [value]);

	const handleFocus = () => {
		if (onFocus) {
			onFocus(id);
		}
	};

	const handleChange = (
		event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
	) => {
		const newValue =
			(event.target as HTMLInputElement).type === "checkbox"
				? (event.target as HTMLInputElement).checked.toString()
				: (event.target as HTMLInputElement).value as string;
		onChange(id, newValue, fieldType);
	};

	const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setLocalValue(event.target.value);
	};

	const handleTextBlur = () => {
		if (localValue !== value) {
			onChange(id, localValue, fieldType);
		}
	};

	const renderFieldLabel = (label: string, fieldRequired?: boolean) => (
		<label style={labelStyles}>
			{label}
			{fieldRequired && <RequiredIndicator>*</RequiredIndicator>}
		</label>
	);

	if (readOnly) {
		const display = formatReadOnlyValue(fieldType, value);
		return (
			<Box>
				{renderFieldLabel(label, fieldRequired)}
				{fieldType === "signature" && value ? (
					<Box component="img" src={value} alt="Signature" sx={{ maxHeight: 80, maxWidth: "100%" }} />
				) : (
					<Typography variant="body2" sx={{ color: "#374151", fontSize: "14px" }}>
						{display}
					</Typography>
				)}
			</Box>
		);
	}

	switch (fieldType) {
		case "shortText": {
			if (isPhoneField(field)) {
				const phoneValue = formatPhoneToXXX(value);
				const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
					onChange(id, formatPhoneToXXX(e.target.value), fieldType);
				};
				return (
					<Box>
						{renderFieldLabel(label, fieldRequired)}
						<Box
							sx={{
								"& .phone-input": {
									width: "100%",
									boxSizing: "border-box",
									height: "36px",
									padding: "8px 12px",
									fontSize: "14px",
									border: "1px solid rgba(0, 0, 0, 0.23)",
									borderRadius: "4px",
									outline: "none",
									fontFamily: "inherit",
								},
								"& .phone-input:focus": {
									borderColor: "primary.main",
									borderWidth: "2px",
								},
								"& .phone-input::placeholder": {
									opacity: 0.42,
								},
							}}
						>
							<InputMask
								mask="999-999-9999"
								value={phoneValue}
								onChange={handlePhoneChange}
								onFocus={handleFocus}
								maskChar={null}
							>
								{(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
									<input
										{...inputProps}
										className="phone-input"
										placeholder={placeholder || "XXX-XXX-XXXX"}
										aria-label={label}
									/>
								)}
							</InputMask>
						</Box>
					</Box>
				);
			}
			return (
				<Box>
					{renderFieldLabel(label, fieldRequired)}
					<TextField
						variant="outlined"
						value={localValue}
						onChange={handleTextChange}
						onBlur={handleTextBlur}
						onFocus={handleFocus}
						fullWidth
						placeholder={placeholder}
						sx={compactInputSx}
					/>
				</Box>
			);
		}
		case "phone": {
			const phoneValue = formatPhoneToXXX(value);
			const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
				onChange(id, formatPhoneToXXX(e.target.value), fieldType);
			};
			return (
				<Box>
					{renderFieldLabel(label, fieldRequired)}
					<Box
						sx={{
							"& .phone-input": {
								width: "100%",
								boxSizing: "border-box",
								height: "36px",
								padding: "8px 12px",
								fontSize: "14px",
								border: "1px solid rgba(0, 0, 0, 0.23)",
								borderRadius: "4px",
								outline: "none",
								fontFamily: "inherit",
							},
							"& .phone-input:focus": {
								borderColor: "primary.main",
								borderWidth: "2px",
							},
							"& .phone-input::placeholder": {
								opacity: 0.42,
							},
						}}
					>
						<InputMask
							mask="999-999-9999"
							value={phoneValue}
							onChange={handlePhoneChange}
							onFocus={handleFocus}
							maskChar={null}
						>
							{(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
								<input
									{...inputProps}
									className="phone-input"
									placeholder={placeholder || "XXX-XXX-XXXX"}
									aria-label={label}
								/>
							)}
						</InputMask>
					</Box>
				</Box>
			);
		}
		case "number": {
			if (isPhoneField(field)) {
				const phoneValue = formatPhoneToXXX(value);
				const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
					onChange(id, formatPhoneToXXX(e.target.value), fieldType);
				};
				return (
					<Box>
						{renderFieldLabel(label, fieldRequired)}
						<Box
							sx={{
								"& .phone-input": {
									width: "100%",
									boxSizing: "border-box",
									height: "36px",
									padding: "8px 12px",
									fontSize: "14px",
									border: "1px solid rgba(0, 0, 0, 0.23)",
									borderRadius: "4px",
									outline: "none",
									fontFamily: "inherit",
								},
								"& .phone-input:focus": {
									borderColor: "primary.main",
									borderWidth: "2px",
								},
								"& .phone-input::placeholder": {
									opacity: 0.42,
								},
							}}
						>
							<InputMask
								mask="999-999-9999"
								value={phoneValue}
								onChange={handlePhoneChange}
								onFocus={handleFocus}
								maskChar={null}
							>
								{(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
									<input
										{...inputProps}
										className="phone-input"
										placeholder={placeholder || "XXX-XXX-XXXX"}
										aria-label={label}
									/>
								)}
							</InputMask>
						</Box>
					</Box>
				);
			}
			return (
				<Box>
					{renderFieldLabel(label, fieldRequired)}
					<TextField
						variant="outlined"
						value={localValue}
						onChange={handleTextChange}
						onBlur={handleTextBlur}
						onFocus={handleFocus}
						fullWidth
						placeholder={placeholder}
						type="number"
						sx={compactInputSx}
					/>
				</Box>
			);
		}
		case "checkbox":
			return (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
					<Checkbox
						checked={value === "true"}
						onChange={e => onChange(id, e.target.checked ? "true" : "false", fieldType)}
						onFocus={handleFocus}
						color="primary"
						sx={{
							padding: "6px",
							"& .MuiSvgIcon-root": { fontSize: 22 },
						}}
					/>
					<Typography component="span" sx={{ fontSize: "14px", color: "#374151" }}>
						{label}
					</Typography>
				</Box>
			);
		case "date": {
			// Normalize any stored value to MM/DD/YYYY for the picker
			const formatToMMDDYYYY = (raw: string): string => {
				if (!raw) return "";
				// ISO date or datetime prefix: YYYY-MM-DD -> MM/DD/YYYY (local calendar date from string)
				const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
				if (isoMatch) {
					const [, year, month, day] = isoMatch;
					return `${month}/${day}/${year}`;
				}
				// Already MM/DD/YYYY — do not interpret as DD/MM/YYYY; ambiguous dates like 10/03/2027
				// are US-style here (matches inputFormat / placeholders).
				return raw;
			};

			const displayValue = formatToMMDDYYYY(value);
			const dayjsValue = displayValue
				? dayjs(displayValue, "MM/DD/YYYY", true)
				: null;
			const pickerValue = dayjsValue?.isValid() ? dayjsValue : null;

			const handleDateChange = (newValue: unknown) => {
				if (!newValue) {
					onChange(id, "", fieldType);
					return;
				}
				// `newValue` is a Dayjs instance from AdapterDayjs
				const anyVal = newValue as { format?: (f: string) => string };
				if (anyVal && typeof anyVal.format === "function") {
					onChange(id, anyVal.format("MM/DD/YYYY"), fieldType);
				}
			};

			return (
				<Box>
					{renderFieldLabel(label, fieldRequired)}
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<MobileDatePicker
							inputFormat="MM/DD/YYYY"
							value={pickerValue}
							onChange={handleDateChange}
							renderInput={(params) => (
								<TextField
									{...params}
									variant="outlined"
									fullWidth
									placeholder={placeholder || "MM/DD/YYYY"}
									sx={compactInputSx}
								/>
							)}
						/>
					</LocalizationProvider>
				</Box>
			);
		}
		case "initials": {
			const suggested = deriveInitialsFromName(signerDisplayName || "");
			return (
				<Box>
					{renderFieldLabel(label || "Initials", fieldRequired)}
					<TextField
						variant="outlined"
						value={localValue}
						onChange={handleTextChange}
						onBlur={handleTextBlur}
						onFocus={handleFocus}
						fullWidth
						placeholder={placeholder || "e.g. JD"}
						inputProps={{ maxLength: 12 }}
						sx={compactInputSx}
					/>
					{suggested ? (
						<Box sx={{ mt: 1 }}>
							<Button
								size="small"
								variant="outlined"
								color="primary"
								onClick={() => onChange(id, suggested, fieldType)}
							>
								Use initials from my name ({suggested})
							</Button>
						</Box>
					) : null}
				</Box>
			);
		}
		case "text":
			return (
				<Box>
					{renderFieldLabel(label, fieldRequired)}
					<TextField
						variant="outlined"
						value={localValue}
						onChange={handleTextChange}
						onBlur={handleTextBlur}
						onFocus={handleFocus}
						fullWidth
						placeholder={placeholder}
						sx={compactInputSx}
					/>
				</Box>
			);
		case "select":
			return (
				<Box>
					{renderFieldLabel(label, fieldRequired)}
					<TextField
						select
						variant="outlined"
						value={value}
						onChange={handleChange}
						onFocus={handleFocus}
						fullWidth
						placeholder={placeholder}
						sx={compactInputSx}
					/>
				</Box>
			);

		case "signature":
			return (
				<Box>
					{renderFieldLabel("Signature", fieldRequired)}
					<SignatureField
						onClick={() => onSignatureClick(id)}
					>
						{value ? (
							<SignatureContent>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										flex: 1,
										pr: 1,
									}}
								>
									<img
										src={value}
										alt="Signature"
										style={{
											maxWidth: "100%",
											maxHeight: "40px",
											objectFit: "contain",
										}}
									/>
								</Box>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										onChange(id, "", fieldType);
									}}
									aria-label="Remove signature"
								>
									<DeleteOutlineIcon fontSize="small" />
								</IconButton>
							</SignatureContent>
						) : (
							<SignaturePlaceholder variant="body2">
								Click to add your signature
							</SignaturePlaceholder>
						)}
					</SignatureField>
				</Box>
			);
		default:
			return (
				<Box>
					{renderFieldLabel(label, fieldRequired)}
					<TextField
						variant="outlined"
						value={value}
						onChange={handleChange}
						onFocus={handleFocus}
						fullWidth
						placeholder={placeholder}
						sx={compactInputSx}
					/>
				</Box>
			);
	}
};

/** Consider a field "completed" for progress and submit (must match DocFormFiller gate).
 *  Unchecked checkboxes are a valid answer (Yes/No, medical conditions that do not apply).
 */
export function isFieldCompleted(field: ValueField): boolean {
	if (field.fieldType === "checkbox") return true;
	const v = (field.value || "").trim();
	if (field.fieldType === "signature") return v.length > 0;
	return v.length > 0;
}

export function isCheckboxChecked(value?: string): boolean {
	const v = (value || "").trim().toLowerCase();
	return v === "true" || v === "yes" || v === "1" || v === "selected" || v === ":selected:" || v === "checked";
}

export function normalizeCheckboxValue(value?: string): string {
	return isCheckboxChecked(value) ? "true" : "false";
}

const DynamicForm: React.FC<DynamicFormProps> = ({ values, onChange, signatureImage, onSaveSignature, onFocus, onFieldInView, signerDisplayName, readOnly = false }) => {
	const [signatureModalOpen, setSignatureModalOpen] = useState(false);
	const [currentSignatureField, setCurrentSignatureField] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState(signatureImage ? 1 : 0);
	const [tempSignature, setTempSignature] = useState<string | null>(null);
	const fieldRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());

	// Group values by fieldGroupTitle (empty string = "Details")
	const groupedValues = useMemo(() => {
		const groups = new Map<string, ValueField[]>();
		for (const field of values) {
			const key = (field.fieldGroupTitle || "").trim() || "\u200b"; // \u200b = zero-width space for "ungrouped"
			if (!groups.has(key)) groups.set(key, []);
			groups.get(key)!.push(field);
		}
		return Array.from(groups.entries()).map(([title, fields]) => ({
			title: title === "\u200b" ? "" : title,
			fields,
		}));
	}, [values]);

	const progressValues = useMemo(
		() => values.filter((field) => field.fieldType !== "checkbox"),
		[values]
	);
	const completedCount = useMemo(() => progressValues.filter(isFieldCompleted).length, [progressValues]);
	const totalCount = progressValues.length;
	const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

	// Scroll sync: when user scrolls form, notify so PDF can scroll to the visible field
	useEffect(() => {
		if (!onFieldInView || fieldRefsMap.current.size === 0) return;
		const observer = new IntersectionObserver(
			(entries) => {
				for (const e of entries) {
					if (!e.isIntersecting) continue;
					const fieldId = (e.target as HTMLElement).dataset?.fieldId;
					if (fieldId) onFieldInView(fieldId);
				}
			},
			{ threshold: 0.25, rootMargin: "-20% 0px -50% 0px" }
		);
		fieldRefsMap.current.forEach((el) => observer.observe(el));
		return () => observer.disconnect();
	}, [onFieldInView, values.length]);

	const handleFocus = (fieldId: string) => {
		if (onFocus) {
			onFocus(fieldId);
		}
	};

	const setFieldRef = useCallback((fieldId: string, el: HTMLDivElement | null) => {
		if (el) fieldRefsMap.current.set(fieldId, el);
		else fieldRefsMap.current.delete(fieldId);
	}, []);

	const handleSignatureClick = (fieldId: string) => {
		handleFocus(fieldId);
		setCurrentSignatureField(fieldId);
		setActiveTab(signatureImage ? 1 : 0);
		setSignatureModalOpen(true);
	};

	const handleSignatureUse = (signatureData: string) => {
		if (currentSignatureField !== null && currentSignatureField !== undefined) {
			const ft =
				values.find((v) => String(v.id) === String(currentSignatureField))?.fieldType ||
				"signature";
			onChange(currentSignatureField, signatureData, ft);
			setSignatureModalOpen(false);
			setCurrentSignatureField(null);
			setTempSignature(null);
		}
	};

	const handleSignatureSave = (signatureData: string) => {
		if (onSaveSignature) {
			onSaveSignature(signatureData);
		}
		setTempSignature(signatureData);
	};

	const handleCanvasSave = async (signatureData: string) => {
		if (onSaveSignature) {
			await onSaveSignature(signatureData);
			// Automatically add the new signature to the current field
			if (currentSignatureField) {
				const ft =
					values.find((v) => String(v.id) === String(currentSignatureField))?.fieldType ||
					"signature";
				onChange(currentSignatureField, signatureData, ft);
			}
			setSignatureModalOpen(false);
			setCurrentSignatureField(null);
			setTempSignature(null);
		}
	};

	const handleSignatureCancel = () => {
		setSignatureModalOpen(false);
		setCurrentSignatureField(null);
		setActiveTab(0);
		setTempSignature(null);
	};

	const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
		setActiveTab(newValue);
	};

	return (
		<>
			<FormContainer>
				{totalCount > 0 && (
					<Box sx={{ mb: 2 }}>
						<Typography variant="body2" sx={{ color: "#6b7280", fontSize: "12px", mb: 0.5 }}>
							Progress: {completedCount} / {totalCount} required fields completed
						</Typography>
						<LinearProgress
							variant="determinate"
							value={progressPercent}
							sx={{
								height: 6,
								borderRadius: 3,
								backgroundColor: "#e5e7eb",
								"& .MuiLinearProgress-bar": {
									borderRadius: 3,
									backgroundColor: "#006ac6",
								},
							}}
						/>
					</Box>
				)}
				{groupedValues.map(({ title, fields: groupFields }) => (
					<FieldGroupSection key={title || "default"}>
						{title ? <FieldGroupTitle>{title}</FieldGroupTitle> : null}
						{clusterCheckboxQuestions(groupFields).map((row) => {
							if (row.kind === "checkboxes") {
								return (
									<FieldContainer key={`q-${row.question}-${row.fields[0].id}`}>
										<ChoiceQuestionTitle>{row.question}</ChoiceQuestionTitle>
										{row.fields.map((field) => (
											<Box
												key={field.id}
												ref={(el) => setFieldRef(field.id, el as HTMLDivElement | null)}
												data-field-id={field.id}
												sx={{ mb: 0.25 }}
											>
												<FieldRenderer
													field={field}
													onChange={onChange}
													onSignatureClick={handleSignatureClick}
													onFocus={handleFocus}
													onFieldInView={onFieldInView}
													compactInputSx={compactInputSx}
													labelStyles={labelStyles}
													signerDisplayName={signerDisplayName}
													readOnly={readOnly}
												/>
											</Box>
										))}
									</FieldContainer>
								);
							}
							return (
								<FieldContainer
									key={row.field.id}
									ref={(el) => setFieldRef(row.field.id, el as HTMLDivElement | null)}
									data-field-id={row.field.id}
								>
									<FieldRenderer
										field={row.field}
										onChange={onChange}
										onSignatureClick={handleSignatureClick}
										onFocus={handleFocus}
										onFieldInView={onFieldInView}
										compactInputSx={compactInputSx}
										labelStyles={labelStyles}
										signerDisplayName={signerDisplayName}
										readOnly={readOnly}
									/>
								</FieldContainer>
							);
						})}
					</FieldGroupSection>
				))}
			</FormContainer>

			{/* Modal para agregar firma */}
			<StyledDialog 
				open={signatureModalOpen} 
				onClose={handleSignatureCancel}
				maxWidth="md"
				fullWidth
				disableScrollLock={true}
				keepMounted={false}
				container={() => document.body}
			>
				<DialogTitle>Create or Update Signature</DialogTitle>
				<DialogContent>
					<Tabs 
						value={activeTab} 
						onChange={handleTabChange}
						indicatorColor="primary"
						textColor="primary"
						variant="fullWidth"
						style={{ marginBottom: "20px" }}
					>
						<Tab label="Draw Signature" />
						<Tab label="Use Existing" />
					</Tabs>

					{activeTab === 0 && (
						<SignatureCanvasComponent
							onSave={handleCanvasSave}
							showSaveButton={true}
							onCancel={handleSignatureCancel}
						/>
					)}

					{activeTab === 1 && (
						<Box>
							<Typography variant="h6" gutterBottom>
								Use Existing Signature
							</Typography>
							<Typography variant="body2" color="textSecondary" gutterBottom>
								Select an existing signature from your account
							</Typography>
							{signatureImage ? (
								<Box 
									style={{ 
										border: "2px solid #e0e0e0",
										borderRadius: "8px",
										padding: "16px",
										textAlign: "center",
										marginTop: "16px"
									}}
								>
									<img 
										src={signatureImage} 
										alt="Existing Signature" 
										style={{ 
											maxWidth: "100%", 
											maxHeight: "100px", 
											objectFit: "contain" 
										}} 
									/>
									<Typography variant="body2" style={{ marginTop: "8px" }}>
										Existing signature from your profile
									</Typography>
								</Box>
							) : (
								<Typography variant="body2" color="textSecondary">
									No existing signatures found. Please create a new one using the "Draw Signature" tab.
								</Typography>
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleSignatureCancel} color="primary">
						Cancel
					</Button>
					{tempSignature && (
						<>
							<Button 
								onClick={() => handleSignatureSave(tempSignature)} 
								color="secondary" 
								variant="outlined"
							>
								Save to Profile
							</Button>
							<Button 
								onClick={() => handleSignatureUse(tempSignature)} 
								color="primary" 
								variant="contained"
							>
								Use Signature
							</Button>
						</>
					)}
					{activeTab === 1 && signatureImage && !tempSignature && (
						<Button 
							onClick={() => handleSignatureUse(signatureImage)} 
							color="primary" 
							variant="contained"
						>
							Use Existing
						</Button>
					)}
				</DialogActions>
			</StyledDialog>
		</>
	);
};

export default DynamicForm;
